import { LiaElement } from '@liasoft/lit-ui';
import { html, nothing } from 'lit';
import { navigate } from '../../router.js';
import { createAccount } from '../api/developer.js';
import { accountPath } from '../accounts.js';
import { loadAccounts } from '../state/context.js';

/**
 * The one form that makes a team: a name, and the person who typed it owns it.
 *
 * Offered on the welcome page and on the list of teams, which is why it is an element rather than a
 * template in either. Creating the team opens it, because its pages are where everything else - buying
 * seats, inviting - happens; the sidebar is re-read on the way so the new team is in it. A person owns
 * at most ten teams, and the eleventh is refused with the server's sentence, shown here.
 */
export class UserCreateTeam extends LiaElement
{
   static properties = {
      /** Whether the form can be put away. On the list it can; on the welcome page it is the page. */
      cancellable: { type: Boolean },

      busy: { state: true },
      error: { state: true }
   };

   constructor()
   {
      super();
      this.cancellable = false;
      this.busy = false;
      this.error = '';
   }

   async create(event)
   {
      event.preventDefault();

      const name = String(new FormData(event.target).get('name') ?? '').trim();

      if (!name)
      {
         return;
      }

      this.busy = true;
      this.error = '';

      try
      {
         const team = await createAccount(name);

         await loadAccounts();
         navigate(accountPath('overview', team.id));
      }
      catch (error)
      {
         this.error = error.message;
      }
      finally
      {
         this.busy = false;
      }
   }

   render()
   {
      return html`
         <lia-card title="Create a team" icon="fa-solid fa-people-group">
            <p class="small text-body-secondary">
               A team is where seats are bought and members invited - for private repositories without
               limit, and for working with others. You own it from the start.
            </p>
            ${this.error
               ? html`<lia-alert variant="danger" icon="fa-solid fa-triangle-exclamation" message=${this.error}></lia-alert>`
               : nothing}
            <form class="d-flex flex-wrap gap-2" @submit=${event => this.create(event)}>
               <input
                  class="form-control form-control-sm flex-grow-1"
                  name="name"
                  placeholder="Team name"
                  aria-label="Team name"
                  maxlength="200"
                  required
                  ?disabled=${this.busy}
               >
               <button class="btn btn-sm btn-primary" type="submit" ?disabled=${this.busy}>
                  ${this.busy ? html`<lia-spinner size="sm" class="me-2"></lia-spinner>Creating` : 'Create the team'}
               </button>
               ${this.cancellable
                  ? html`
                     <button
                        class="btn btn-sm btn-link"
                        type="button"
                        @click=${() => this.dispatchEvent(new CustomEvent('user-create-team-cancel', { bubbles: true }))}
                     >Cancel</button>`
                  : nothing}
            </form>
         </lia-card>`;
   }
}

customElements.define('user-create-team', UserCreateTeam);
